import React, { useEffect, useState } from 'react';
import { api } from '../services/api';
import { proxied } from '../services/assets';
import { getAppearAs, setAppearAs } from '../services/worldSocket';
import { edgeInset } from '../config';

// AppearAs — a member picks which of their OWNED avatars they appear as in the
// world (the name + headshot other players see on their figure). Mounted only for
// members (App gates on !IS_GUEST). DOM <img> thumbnails, so no WebGL/CORS concern
// here (that's only the in-scene 3D headshot).
//
// A FACELESS AVATAR IS STILL PICKABLE. This used to filter to avatars with a
// profile image, which quietly made the newest avatar — the one you just made,
// with nothing generated for it yet — unselectable. That is backwards for Stage
// One in particular: THE COME UP is the flow that gives an avatar its look, its
// name and its first track, so an avatar with no face is precisely the avatar
// the chain exists to onboard. Filtering on "has a face" meant the onboarding
// could never be played by its own subject.
//
// Nothing downstream needs the image either: Peers' <Headshot> renders nothing
// when the URL is missing and the peer falls back to the capsule marker + name
// pill, which is exactly the "member with no track" presence tier. So no model
// and no image has to be generated first — a monogram covers the picker, and
// the world already covers itself.

// edgeInset, not a bare 16px: on an ultrawide the viewport edge is in the
// periphery, so this tracks the HUD's centred column instead (config.HUD_MAX).
const wrap = { position: 'fixed', left: edgeInset(16, 'left'), top: 'calc(max(16px, env(safe-area-inset-top)) + 60px)', zIndex: 40, pointerEvents: 'auto', fontFamily: 'system-ui, sans-serif' };
const trigger = { display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(20,12,6,0.72)', border: '1px solid rgba(255,178,94,0.4)', borderRadius: 999, padding: '3px 8px 3px 3px', color: '#ffd27d', font: '600 10.5px system-ui', cursor: 'pointer', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' };
const thumb = { width: 16, height: 16, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 };
const panel = { marginTop: 8, width: 'min(300px, 78vw)', maxHeight: '52vh', overflowY: 'auto', background: 'rgba(16,10,6,0.96)', border: '1px solid rgba(255,178,94,0.4)', borderRadius: 14, padding: 12, backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', boxShadow: '0 18px 50px rgba(0,0,0,0.5)' };
const panelHead = { fontSize: 11, letterSpacing: 1.4, textTransform: 'uppercase', color: 'rgba(255,178,94,0.8)', marginBottom: 10 };
const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(78px, 1fr))', gap: 8 };
const cell = { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: '8px 4px', cursor: 'pointer', color: '#ffe9c4' };
const cellImg = { width: 52, height: 52, borderRadius: '50%', objectFit: 'cover' };
const cellName = { fontSize: 11, maxWidth: 72, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

// A face, or the next best thing. An avatar that hasn't been given a look yet
// gets its initial on the district's own gradient — legible at 16px, and it
// reads as "not yet" rather than as a broken image.
function Face({ avatar, style, monogramFont }) {
  if (avatar?.profile_image_url) {
    return <img src={proxied(avatar.profile_image_url)} alt="" style={style} />;
  }
  return (
    <span style={{
      ...style, display: 'grid', placeItems: 'center',
      background: 'linear-gradient(160deg,#ffb25e,#ff5e78)', color: '#1a0f00',
      font: monogramFont, textTransform: 'uppercase',
    }}>{(avatar?.name || '?').trim().charAt(0)}</span>
  );
}

export default function AppearAs() {
  const [avatars, setAvatars] = useState([]);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(() => getAppearAs());

  useEffect(() => {
    let dead = false;
    api.myAvatars()
      .then((d) => { if (!dead) setAvatars(d?.avatars || []); })
      .catch(() => {});
    return () => { dead = true; };
  }, []);

  // ONE avatar = no question to ask (Khari, 2026-08-03: "I only have a single
  // avatar.. I shouldn't be asked this"). The only artist you own is who you
  // are: commit it so the socket re-auths and the server's `me` resolves —
  // fresh salon converts were hitting "pick which artist" walls with exactly
  // one possible answer. The picker UI remains for the day there are two.
  useEffect(() => {
    if (avatars.length !== 1) return;
    const only = String(avatars[0].avatar_id);
    const saved = getAppearAs();
    if (saved === only) return;
    setCurrent(only);
    setAppearAs(avatars[0].avatar_id);
  }, [avatars]);

  if (!avatars.length) return null; // owns no avatars at all → nothing to pick

  const currentAvatar = avatars.find((a) => String(a.avatar_id) === String(current)) || avatars[0];
  const choose = (a) => { setCurrent(String(a.avatar_id)); setAppearAs(a.avatar_id); setOpen(false); };

  return (
    <div style={wrap}>
      <button style={trigger} onClick={() => setOpen((o) => !o)} title="Choose who you appear as">
        <Face avatar={currentAvatar} style={thumb} monogramFont="800 8px system-ui" />
        <span style={{ maxWidth: 78, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{currentAvatar.name}</span>
        <span style={{ opacity: 0.7 }}>▾</span>
      </button>
      {open && (
        <div style={panel}>
          <div style={panelHead}>Appear as</div>
          <div style={grid}>
            {avatars.map((a) => {
              const sel = String(a.avatar_id) === String(currentAvatar.avatar_id);
              return (
                <button key={a.avatar_id} onClick={() => choose(a)} style={{ ...cell, border: `1px solid ${sel ? '#ffb25e' : 'rgba(255,255,255,0.14)'}` }}>
                  <Face avatar={a} style={cellImg} monogramFont="800 20px system-ui" />
                  <span style={cellName}>{a.name}</span>
                  {!a.profile_image_url && (
                    <span style={{ fontSize: 9, opacity: 0.5, letterSpacing: 0.3 }}>no face yet</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
